import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { BehaviorSubject, startWith, switchMap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import { DynamicFormsService } from '@services/dynamic-forms.service';

@Component({
  selector: 'cvx-dynamic-forms-list-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './dynamic-forms-list-page.component.html',
  styleUrl: './dynamic-forms-list-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicFormsListPageComponent {
  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly layoutControl = new FormControl<'all' | 'vertical' | 'grid'>('all', { nonNullable: true });
  protected readonly sortControl = new FormControl<'nameAsc' | 'nameDesc' | 'modifiedDesc' | 'modifiedAsc'>('nameAsc', {
    nonNullable: true,
  });

  private readonly pageIndex = signal(0);
  private readonly pageSize = signal(8);

  private readonly service = inject(DynamicFormsService);
  private readonly router = inject(Router);

  private readonly refresh$ = new BehaviorSubject<void>(undefined);
  private readonly allForms = toSignal(this.refresh$.pipe(switchMap(() => this.service.forms$)), { initialValue: [] });
  private readonly searchValue = toSignal(this.searchControl.valueChanges.pipe(startWith(this.searchControl.value)), {
    initialValue: '',
  });
  private readonly layoutValue = toSignal(this.layoutControl.valueChanges.pipe(startWith(this.layoutControl.value)), {
    initialValue: 'all' as const,
  });
  private readonly sortValue = toSignal(this.sortControl.valueChanges.pipe(startWith(this.sortControl.value)), {
    initialValue: 'nameAsc' as const,
  });

  protected readonly forms = computed(() => {
    const term = (this.searchValue() ?? '').toLowerCase().trim();
    const layout = this.layoutValue();
    const sort = this.sortValue();

    const filtered = this.allForms().filter((form) => {
      const textMatch =
        !term ||
        form.name.toLowerCase().includes(term) ||
        (form.description ?? '').toLowerCase().includes(term) ||
        (form.pageTitle ?? '').toLowerCase().includes(term);
      const layoutType = form.layout?.type ?? 'vertical';
      const layoutMatch = layout === 'all' || layoutType === layout;
      return textMatch && layoutMatch;
    });

    return [...filtered].sort((a, b) => {
      if (sort === 'nameAsc') return a.name.localeCompare(b.name);
      if (sort === 'nameDesc') return b.name.localeCompare(a.name);

      const aDate = new Date(a.modifiedOn ?? a.createdOn ?? 0).getTime();
      const bDate = new Date(b.modifiedOn ?? b.createdOn ?? 0).getTime();
      return sort === 'modifiedDesc' ? bDate - aDate : aDate - bDate;
    });
  });

  protected readonly totalCount = computed(() => this.forms().length);
  protected readonly pagedForms = computed(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.forms().slice(start, start + this.pageSize());
  });

  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize())));

  protected createForm(): void {
    this.router.navigate(['/tools/dynamic-forms/new/builder']);
  }

  protected toDetails(id: string): void {
    this.router.navigate(['/tools/dynamic-forms', id, 'details']);
  }

  protected toBuilder(id: string): void {
    this.router.navigate(['/tools/dynamic-forms', id, 'builder']);
  }

  protected toRuntime(id: string): void {
    this.router.navigate(['/tools/dynamic-forms', id, 'runtime']);
  }

  protected delete(id: string): void {
    this.service.delete(id).subscribe(() => this.refresh$.next());
  }

  protected goPrevious(): void {
    this.pageIndex.update((value) => Math.max(0, value - 1));
  }

  protected goNext(): void {
    this.pageIndex.update((value) => Math.min(this.totalPages() - 1, value + 1));
  }
}
